// React i hooks
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// Navigacija
import { Routes } from "../../navigation";
import { Routes as FleetWarehouseRoutes } from "ui/modules/fleet/warehouses/navigation";

// Helpers
import { transformRoute, useTranslate } from "helpers";

// API
import { deleteWarehouseAPI, getFleetWarehousesAPI } from "services/api/fleet/warehouses/api";
import { Dialog } from "ui/components/Dialog";
import { FleetWarehouseRow, FleetWarehouseSearchFormValues } from "services/api/fleet/warehouses/types";
import { initialFleetWarehouseSearchForm } from "services/api/fleet/warehouses/consts";
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";

// Komponente
import { WarehouseSearchPageCmp, WarehouseSearchPageProps } from "./types";
import { Breadcrumb } from "ui/components/Breadcrumb";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { Pagination } from "services/search";
import { useSelectedItemsChanges } from "ui/components/Table/hooks";
import { NoResults } from "ui/components/NoResults";
import { Button } from "ui/components/Button/Button";

// Stranice / Komponente pretrage
import WarehouseSearchResults from "./components/WarehouseSearchResults/WarehouseSearchResults";
import WarehouseSearchFilters from "./components/WarehouseSearchFilters/WarehouseSearchFilters";

// Konstante
import { FLEET_WAREHOUSE_SEARCH_PAGE_TITLE } from "ui/components/PageTitle/consts";
import WarehouseEditCard from "../WarehouseDetailPage/components/WarehouseDetailCard/components/WarehouseEditCard/WarehouseEditCard";

const WarehouseSearchPage: WarehouseSearchPageCmp = (
  props: WarehouseSearchPageProps
) => {
  const { t } = useTranslate();

  const [searchResults, setSearchResults] = useState<
    DataWithPagination<FleetWarehouseRow>
  >(initialDataWithPagination);

  // Filtri se pamte ovdje, pa ih zadrže i paginacija i osvježavanje nakon unosa.
  const [filterValues, setFilterValues] = useState<FleetWarehouseSearchFormValues>(
    initialFleetWarehouseSearchForm
  );
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);
  const [deleteWarehouseId, setDeleteWarehouseId] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSearchResult(searchResults.pagination);
  }, []);

  const fetchSearchResult = async (
    pagination: Pagination,
    values: FleetWarehouseSearchFormValues = filterValues
  ) => {
    setIsLoading(true);
    try {
      const results = await getFleetWarehousesAPI(pagination, values.search, values.address, values.contactId);

      setSearchResults(results);
    } catch {
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleRowClick = useCallback(async (id: string) => {
    navigate(
      transformRoute(FleetWarehouseRoutes.FleetWarehouseDetailPageRouter, {
        id,
      })
    );
  }, []);

  const handleAddNew = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      // setTehnicalInspectionId("");
      setShowAddNewModal(true);
    },
    []
  );

  const handleEditSuccess = useCallback(() => {
    fetchSearchResult(searchResults.pagination);
    // setTehnicalInspectionId(null);
    setShowAddNewModal(false);
  }, []);

  const handleSearchChange = useCallback(
    (values: FleetWarehouseSearchFormValues) => {
      setFilterValues(values);
      fetchSearchResult(searchResults.pagination, values);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchResults.pagination]
  );

  const handleSelectedChange = useSelectedItemsChanges(
    selectedRows,
    setSelectedRows
  );

  const handlePaginationChange = (newPagination: Pagination) => {
    fetchSearchResult(newPagination);
  };

  // Klikovi u retku (telefon, email, karta, brisanje) ne smiju otvoriti i detalje skladišta.
  const handlePhoneClick = useCallback((event: React.MouseEvent<HTMLSpanElement>, phone: string) => {
    event.stopPropagation();
    event.preventDefault();
    window.location.href = `tel:${phone}`;
  }, []);

  const handleEmailClick = useCallback((event: React.MouseEvent<HTMLSpanElement>, emailAddress: string) => {
    event.stopPropagation();
    event.preventDefault();
    window.location.href = `mailto:${emailAddress}`;
  }, []);

  // Bez koordinata karta traži po upisanoj adresi.
  const handleGoogleMapClick = useCallback((event: React.MouseEvent<HTMLSpanElement>, row: FleetWarehouseRow) => {
    event.stopPropagation();
    event.preventDefault();
    const query =
      row.addressLatitude != null && row.addressLongitude != null
        ? `${row.addressLatitude},${row.addressLongitude}`
        : encodeURIComponent([row.addressLine1, row.addressPostalCode, row.addressCity, row.addressCountry].filter(Boolean).join(", "));
    window.open(`https://www.google.com/maps/search/?api=1&query=${query}`, "_blank", "noopener,noreferrer");
  }, []);

  const handleDeleteClick = useCallback((event: React.MouseEvent<HTMLSpanElement>, id: string) => {
    event.stopPropagation();
    event.preventDefault();
    setDeleteWarehouseId(id);
  }, []);

  const handleDeleteDialogConfirm = async () => {
    if (!deleteWarehouseId) return;

    try {
      await deleteWarehouseAPI(deleteWarehouseId);
      await fetchSearchResult(searchResults.pagination);
    } catch {
      // greška se prikazuje kroz snackbar
    }
    setDeleteWarehouseId(null);
  };

  const handleDeleteDialogClose = useCallback(() => {
    setDeleteWarehouseId(null);
  }, []);

  return (
    <>
      <div className="">
        <div className="bg-white rounded-t-md border border-b mb-2">
          <PageHeader>
            <div className="flex items-center justify-between">
              <div>
                <PageTitle
                  title={FLEET_WAREHOUSE_SEARCH_PAGE_TITLE.text}
                  icon={FLEET_WAREHOUSE_SEARCH_PAGE_TITLE.icon}
                  iconColor={FLEET_WAREHOUSE_SEARCH_PAGE_TITLE.iconColor}
                />
                <Breadcrumb />
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="primary"
                  children={t("admin.fleet.warehouse.search.add")}
                  startIcon="/media/icons/plus.svg"
                  onClick={handleAddNew}
                />
              </div>
            </div>
          </PageHeader>
        </div>
        <div className="px-0 pt-2 mx-[-1px] xl:px-6">
          <WarehouseSearchFilters
            onSearchChange={handleSearchChange}
          />
          {!isLoading && searchResults.pagination.totalPages === 0 ? (
            <NoResults message={t("admin.fleet.warehouse.search.noresults")} />
          ) : (
            <WarehouseSearchResults
              searchResults={searchResults}
              selectedRows={selectedRows}
              isLoading={isLoading}
              onRowClick={handleRowClick}
              onPaginationChange={handlePaginationChange}
              onSelectedChange={handleSelectedChange}
              onDeleteClick={handleDeleteClick}
              onEmailClick={handleEmailClick}
              onPhoneClick={handlePhoneClick}
              onGoogleMapClick={handleGoogleMapClick}
            />
          )}
        </div>
      </div>
      <WarehouseEditCard
        warehouseId={null}
        show={showAddNewModal}
        onClose={() => setShowAddNewModal(false)}
        onSuccess={handleEditSuccess}
      />
      <Dialog
        open={!!deleteWarehouseId}
        title="Brisanje skladišta"
        message="Jeste li sigurni da želite obrisati ovo skladište?"
        confirmText="Obriši"
        cancelText="Odustani"
        onConfirm={handleDeleteDialogConfirm}
        onCancel={handleDeleteDialogClose}
      />
    </>
  );
};

WarehouseSearchPage.route = Routes.FleetWarehouseSearchPageRouter;

export default WarehouseSearchPage;
